import React, { useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import ApartmentIcon from "@mui/icons-material/Apartment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LanguageIcon from "@mui/icons-material/Language";
import PublicIcon from "@mui/icons-material/Public";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LogoutIcon from "@mui/icons-material/Logout";
import PeopleIcon from "@mui/icons-material/People";
import { useNavigate, useLocation } from "react-router-dom";
import { useUsuario } from "../../Hooks/UsuarioHook";
import { app_base_url } from "../../Utils/Urls";
import {
  resolverSistemaDisplay,
  iniciaisSistema,
  hueSistema,
  hrefDoSistema,
} from "../../Utils/sistemaDisplay";
import {
  agruparPorCategoria,
  metadadosCategoria,
} from "../../Utils/categoriasSistema";
import { ThemeToggle } from "../ThemeToggle";
import { FooterSuporte } from "../FooterSuporte";
import { TrocarContaModal } from "../TrocarContaModal";
import { ConfirmarSaida } from "../ConfirmarSaida";

interface TopBarProps {
  onMenuClick: () => void;
}

function tituloDaPagina(pathname: string): string {
  if (pathname.includes("/usuarios")) return "Usuários";
  if (pathname.includes("/perfil")) return "Perfil";
  if (pathname.includes("/criar-usuario")) return "Novo Usuário";
  if (pathname.includes("/sistema/")) return "Sistema";
  return "Sistemas";
}

function formatarCnpj(v?: string): string {
  if (!v) return "";
  const d = v.replace(/\D/g, "");
  if (d.length !== 14) return v;
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3.$4-$5");
}

function normalizarUrl(u: string): string {
  const v = u.trim();
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const { usuario, sair } = useUsuario();
  const navigate = useNavigate();
  const location = useLocation();

  const entidade = usuario?.entidade as any;
  const titulo = tituloDaPagina(location.pathname);
  const cnpj = formatarCnpj(entidade?.CNPJ);

  const [anchorSites, setAnchorSites] = useState<null | HTMLElement>(null);
  const [anchorUser, setAnchorUser] = useState<null | HTMLElement>(null);
  const [trocarAberto, setTrocarAberto] = useState(false);
  const [sairAberto, setSairAberto] = useState(false);
  const [buscaSistema, setBuscaSistema] = useState("");

  // sistemas filtrados pela busca rápida (por título já resolvido)
  const termo = buscaSistema.trim().toLowerCase();
  const sistemasFiltrados = (usuario?.sistemas ?? []).filter((s) =>
    resolverSistemaDisplay(s, entidade?.banco).titulo.toLowerCase().includes(termo)
  );
  const jaTemPortalServidor = (usuario?.sistemas ?? []).some((s) =>
    resolverSistemaDisplay(s, entidade?.banco)
      .titulo.toLowerCase()
      .includes("portal do servidor")
  );

  // Mesmo agrupamento da home e da sidebar (Utils/categoriasSistema.ts)
  const gruposSistemas = agruparPorCategoria(sistemasFiltrados);

  const irPara = (path: string) => {
    setAnchorUser(null);
    navigate(path);
  };

  // href do sistema — helper compartilhado com a sidebar (trata URL sem protocolo)
  const hrefSistema = hrefDoSistema;

  const sites = [
    { label: "Portal da Transparência", url: entidade?.portal as string, tipo: "portal" as const },
    { label: "Site oficial", url: entidade?.site as string, tipo: "site" as const },
    { label: "Site alternativo", url: entidade?.site2 as string, tipo: "site" as const },
  ].filter((s) => typeof s.url === "string" && s.url.trim() !== "");

  const abrirSite = (url: string) => {
    window.open(normalizarUrl(url), "_blank", "noopener");
    setAnchorSites(null);
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        px: { xs: 1.5, md: 3 },
        height: 64,
        flexShrink: 0,
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        boxShadow: (t) =>
          t.palette.mode === "dark"
            ? "0 2px 10px rgba(0,0,0,0.35)"
            : "0 2px 10px rgba(15,23,42,0.05)",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      {/* Hambúrguer (mobile) */}
      <IconButton
        onClick={onMenuClick}
        sx={{ display: { xs: "inline-flex", md: "none" } }}
      >
        <MenuIcon />
      </IconButton>

      {/* Prefeitura / entidade — clicável: abre os sites */}
      <Tooltip title={sites.length ? "Ver os sites da prefeitura" : ""} arrow>
        <Box
          onClick={(e) => sites.length && setAnchorSites(e.currentTarget)}
          role={sites.length ? "button" : undefined}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            minWidth: 0,
            px: 1,
            py: 0.5,
            mx: -1,
            borderRadius: 2,
            cursor: sites.length ? "pointer" : "default",
            transition: "background-color .15s",
            "&:hover": { bgcolor: sites.length ? "action.hover" : "transparent" },
            "&:hover .prefArrow": { opacity: 1 },
          }}
        >
          {entidade?.logo ? (
            <Box
              component="img"
              src={entidade.logo}
              alt={entidade.titulo}
              sx={{ height: 40, width: "auto", objectFit: "contain" }}
            />
          ) : (
            <Avatar variant="rounded" sx={{ bgcolor: "action.hover", color: "text.secondary" }}>
              <ApartmentIcon />
            </Avatar>
          )}
          {/* Nome/CNPJ: some no mobile pra não amassar (fica só a logo) */}
          <Box sx={{ minWidth: 0, display: { xs: "none", sm: "block" } }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, lineHeight: 1.2, display: "flex", alignItems: "center", gap: 0.5 }}
              noWrap
            >
              {entidade?.titulo ?? "Login Único"}
              {sites.length > 0 && (
                <KeyboardArrowDownIcon
                  className="prefArrow"
                  sx={{ fontSize: 18, color: "text.secondary", opacity: 0.5, transition: "opacity .15s" }}
                />
              )}
            </Typography>
            {cnpj && (
              <Typography variant="caption" color="text.secondary" noWrap>
                CNPJ: {cnpj}
              </Typography>
            )}
          </Box>
        </Box>
      </Tooltip>

      {/* Menu dos sites da prefeitura */}
      <Menu
        anchorEl={anchorSites}
        open={!!anchorSites}
        onClose={() => setAnchorSites(null)}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 260, mt: 0.5 } } }}
      >
        <Typography
          sx={{
            px: 2,
            py: 1,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.5,
            color: "text.secondary",
            textTransform: "uppercase",
          }}
        >
          Sites da prefeitura
        </Typography>
        <Divider />
        {sites.map((s) => (
          <MenuItem key={s.url} onClick={() => abrirSite(s.url)}>
            <ListItemIcon>
              {s.tipo === "portal" ? (
                <AccountBalanceIcon fontSize="small" sx={{ color: "#15803d" }} />
              ) : (
                <LanguageIcon fontSize="small" sx={{ color: "primary.main" }} />
              )}
            </ListItemIcon>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.2 }}>{s.label}</Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.2 }} noWrap>
                {s.url}
              </Typography>
            </Box>
          </MenuItem>
        ))}
        {sites.length > 1 && (
          <>
            <Divider />
            <MenuItem onClick={() => { sites.forEach((s) => window.open(normalizarUrl(s.url), "_blank", "noopener")); setAnchorSites(null); }}>
              <ListItemIcon>
                <PublicIcon fontSize="small" />
              </ListItemIcon>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>Abrir todos os sites</Typography>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Título da página (centralizado, sem bloquear cliques) */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          display: { xs: "none", md: "block" },
          pointerEvents: "none",
        }}
      >
        {/* Título da página na barra é CROMO, não o cabeçalho da tela: quem manda
            é o título dentro do painel de conteúdo. Por isso vai discreto — antes
            estava 18px/800, maior que o próprio cabeçalho do painel. */}
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 14,
            letterSpacing: 0.3,
            color: "text.secondary",
          }}
        >
          {titulo}
        </Typography>
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* Ações: suporte, tema, usuário */}
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.25, md: 0.75 } }}>
        <FooterSuporte variant="inline" iconOnly />
        <ThemeToggle variant="icon" />

        <Tooltip title="Minha conta" arrow>
          <Box
            onClick={(e) => setAnchorUser(e.currentTarget)}
            role="button"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 0.5,
              borderRadius: 2,
              cursor: "pointer",
              transition: "background-color .15s",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Avatar
              src={usuario?.usuario?.foto ?? undefined}
              sx={{ width: 36, height: 36, bgcolor: "primary.main", fontSize: 15 }}
            >
              {!usuario?.usuario?.foto &&
                usuario?.usuario?.nome?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box sx={{ display: { xs: "none", sm: "block" }, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.1 }}>
                Logado como
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                {usuario?.usuario?.nome}
              </Typography>
            </Box>
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          </Box>
        </Tooltip>
      </Box>

      {/* Menu da conta do usuário */}
      <Menu
        anchorEl={anchorUser}
        open={!!anchorUser}
        onClose={() => setAnchorUser(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 300, mt: 0.5 } } }}
      >
        {/* Cabeçalho */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5 }}>
          <Avatar
            src={usuario?.usuario?.foto ?? undefined}
            sx={{ width: 44, height: 44, bgcolor: "primary.main" }}
          >
            {!usuario?.usuario?.foto && usuario?.usuario?.nome?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {usuario?.usuario?.nome}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
              {entidade?.titulo}
            </Typography>
          </Box>
        </Box>
        <Divider />

        {/* Acessar outra conta — ação principal, em destaque */}
        <MenuItem
          onClick={() => {
            setAnchorUser(null);
            setTrocarAberto(true);
          }}
          sx={{
            mx: 1,
            my: 1,
            borderRadius: 2,
            bgcolor: "primary.main",
            color: "#fff",
            "&:hover": { bgcolor: "primary.dark" },
            "& .MuiListItemIcon-root": { color: "#fff" },
          }}
        >
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Acessar outra conta</Typography>
        </MenuItem>
        <Divider />

        {/* Sistemas disponíveis */}
        {usuario?.sistemas?.length > 0 && (
          <>
            <Typography
              sx={{
                px: 2,
                pt: 1,
                pb: 0.5,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 0.5,
                color: "text.secondary",
                textTransform: "uppercase",
              }}
            >
              Sistemas disponíveis
            </Typography>

            {/* Busca rápida (aparece quando há vários sistemas) */}
            {usuario.sistemas.length > 4 && (
              <Box sx={{ px: 2, pb: 0.5 }}>
                <TextField
                  value={buscaSistema}
                  onChange={(e) => setBuscaSistema(e.target.value)}
                  placeholder="Buscar sistema..."
                  size="small"
                  fullWidth
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.85rem" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}

            <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
              {gruposSistemas.map(({ categoria, sistemas }) => (
                <Box key={categoria}>
                  {/* Cabeçalho da categoria: bolinha da cor + rótulo miúdo,
                      igual à sidebar. Enxuto porque o menu já rola. */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 2,
                      pt: 1,
                      pb: 0.25,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        flexShrink: 0,
                        bgcolor: `hsl(${metadadosCategoria(categoria).hue} 65% 55%)`,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        color: "text.disabled",
                      }}
                    >
                      {categoria}
                    </Typography>
                  </Box>

                  {sistemas.map((sistema) => {
                    const { titulo, logoSrc } = resolverSistemaDisplay(
                      sistema,
                      entidade?.banco
                    );
                    const hue = hueSistema(titulo);
                    return (
                      <MenuItem
                        key={sistema.id}
                        component="a"
                        href={hrefSistema(sistema.url)}
                        onClick={() => setAnchorUser(null)}
                      >
                    <ListItemIcon>
                      {/* mesmo fallback da home e da sidebar: iniciais coloridas */}
                      <Avatar
                        variant="rounded"
                        src={logoSrc || undefined}
                        alt={titulo}
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 1.5,
                          fontSize: "0.62rem",
                          fontWeight: 800,
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? `hsl(${hue} 42% 22%)`
                              : `hsl(${hue} 72% 94%)`,
                          color: (t) =>
                            t.palette.mode === "dark"
                              ? `hsl(${hue} 70% 78%)`
                              : `hsl(${hue} 55% 32%)`,
                          "& img": { objectFit: "contain" },
                        }}
                      >
                        {iniciaisSistema(titulo)}
                      </Avatar>
                    </ListItemIcon>
                    <Typography sx={{ fontSize: 14 }}>{titulo}</Typography>
                      </MenuItem>
                    );
                  })}
                </Box>
              ))}
              {usuario?.usuario?.servidor &&
                !jaTemPortalServidor &&
                "portal do servidor".includes(termo) && (
                  <MenuItem
                    component="a"
                    href="https://solucoes.sinsoft.com.br/portal-do-servidor"
                    onClick={() => setAnchorUser(null)}
                  >
                    <ListItemIcon>
                      <PeopleIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography sx={{ fontSize: 14 }}>Portal do Servidor</Typography>
                  </MenuItem>
                )}

              {sistemasFiltrados.length === 0 &&
                !(usuario?.usuario?.servidor && !jaTemPortalServidor && "portal do servidor".includes(termo)) && (
                  <Typography
                    sx={{ px: 2, py: 1.5, fontSize: 13, color: "text.disabled" }}
                  >
                    Nenhum sistema encontrado.
                  </Typography>
                )}
            </Box>
            <Divider />
          </>
        )}

        {/* Ações */}
        <MenuItem onClick={() => irPara(`${app_base_url}/perfil`)}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <Typography sx={{ fontSize: 14 }}>Ver meu perfil</Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorUser(null);
            setSairAberto(true);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: "error.main" }} />
          </ListItemIcon>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Sair</Typography>
        </MenuItem>
      </Menu>

      <TrocarContaModal open={trocarAberto} onClose={() => setTrocarAberto(false)} />
      <ConfirmarSaida
        open={sairAberto}
        onClose={() => setSairAberto(false)}
        onConfirm={() => {
          setSairAberto(false);
          sair();
        }}
      />
    </Box>
  );
}
